import cn from "classnames";
import { type FC, useState } from "react";
import { type ContentFileDto, isSocialPlatform, Platform } from "shared";
import { PlatformIcon } from "../common/PlatformIcon";
import { useCompany } from "../company/useCompany";
import { useFeatureFlag } from "../hooks/use-app-metadata";
import { FacebookPreview } from "./platforms/FacebookPreview";
import { InstagramPreview } from "./platforms/InstagramPreview";
import { LinkedInPreview } from "./platforms/LinkedInPreview";
import { ThreadsPreview } from "./platforms/ThreadsPreview";
import { TikTokPreview } from "./platforms/TikTokPreview";
import { YouTubePreview } from "./platforms/YouTubePreview";
import "./PostPreviewContainer.css";

interface PostPreviewContainerProps {
	content: string;
	files: ContentFileDto[];
	initialPlatform?: Platform;
	thumbnailFile?: ContentFileDto;
}

const SOCIAL_PLATFORMS = [
	Platform.INSTAGRAM,
	Platform.FACEBOOK,
	Platform.LINKEDIN,
	Platform.THREADS,
	Platform.TIKTOK,
	Platform.YOUTUBE,
] as const;

export const PostPreviewContainer: FC<PostPreviewContainerProps> = ({
	content,
	files,
	initialPlatform,
	thumbnailFile,
}) => {
	const company = useCompany();
	const isTikTokEnabled = useFeatureFlag("TikTokIntegration");
	const isYouTubeEnabled = useFeatureFlag("YouTubeIntegration");
	const [selectedPlatform, setSelectedPlatform] = useState<Platform>(() => {
		if (initialPlatform && isSocialPlatform(initialPlatform)) {
			return initialPlatform;
		}
		return Platform.INSTAGRAM;
	});
	const previewPlatforms = SOCIAL_PLATFORMS.filter((platform) => {
		if (platform === Platform.TIKTOK) {
			return isTikTokEnabled || initialPlatform === Platform.TIKTOK;
		}
		if (platform === Platform.YOUTUBE) {
			return isYouTubeEnabled || initialPlatform === Platform.YOUTUBE;
		}
		return true;
	});

	const renderPreview = () => {
		const previewProps = {
			companyName: company.name,
			content,
			files,
		};

		switch (selectedPlatform) {
			case Platform.INSTAGRAM:
				return <InstagramPreview {...previewProps} />;
			case Platform.FACEBOOK:
				return <FacebookPreview {...previewProps} />;
			case Platform.LINKEDIN:
				return <LinkedInPreview {...previewProps} />;
			case Platform.THREADS:
				return <ThreadsPreview {...previewProps} />;
			case Platform.TIKTOK:
				return <TikTokPreview {...previewProps} />;
			case Platform.YOUTUBE:
				return (
					<YouTubePreview {...previewProps} thumbnailFile={thumbnailFile} />
				);
			default:
				return <InstagramPreview {...previewProps} />;
		}
	};

	return (
		<div className="post-preview-container">
			<div className="post-preview-tabs">
				{previewPlatforms.map((platform) => (
					<button
						key={platform}
						type="button"
						className={cn("post-preview-tab", {
							"post-preview-tab--active": selectedPlatform === platform,
						})}
						onClick={() => setSelectedPlatform(platform)}
					>
						<PlatformIcon value={platform} size={20} />
						<span className="post-preview-tab__label">{platform}</span>
					</button>
				))}
			</div>

			<div className="post-preview-content">{renderPreview()}</div>
		</div>
	);
};
